import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Avatar,
    Chip,
    Tabs,
    Tab,
    Select,
    MenuItem,
    Menu,
    Alert,
    InputAdornment,
    Card,
    Button
} from "@mui/material";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";
import { encryptId } from "../helper/idCodec.js";
import "./Truck-Owners.css";

// Helper function for initials
const getInitials = (name) => {
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    if (parts.length > 1)
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    return "";
};

// Tab Panel
function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

function TruckOwners() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [usersPerPage, setUsersPerPage] = useState(10);
    const [tabValue, setTabValue] = useState(0);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
    const [deleteSuccessMessage, setDeleteSuccessMessage] = useState(null);

    const navigate = useNavigate();
    const openUserMenu = Boolean(userMenuAnchorEl);

    // Fetch owners
    const fetchTruckOwners = useCallback(async () => {
        setLoading(true);
        setError(null);
        setDeleteErrorMessage(null);
        setDeleteSuccessMessage(null);

        try {
            const [response] = await Promise.all([
                getData(API_ENDPOINTS.getTruckOwners),
                new Promise((resolve) => setTimeout(resolve, 1000)),
            ]);

            if (response.status === "SUCCESS" && response.result) {
                const transformedUsers = response.result.map((owner) => ({
                    id: owner.owner_id,
                    name: `${owner.first_name || ""} ${owner.last_name || ""}`.trim(),
                    email: owner.email || "N/A",
                    mobile_num: owner.mobile_num || "N/A",
                    avatar: owner.pic || undefined,
                    status: owner.is_verified ? "Active" : "Inactive",
                    rating: owner.Trucks?.[0]?.average_rating || 0,
                    createdAt: owner.createdAt || "",
                }));
                setUsers(transformedUsers);
            } else {
                setError(response.message || "Failed to fetch truck owners.");
            }
        } catch (err) {
            setError("An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTruckOwners();
    }, [fetchTruckOwners]);

    // Filter + Search
    const filteredUsers = useMemo(() => {
        let currentUsers = users;
        if (appliedSearchTerm) {
            currentUsers = currentUsers.filter(
                (user) =>
                    user.name.toLowerCase().includes(appliedSearchTerm.toLowerCase()) ||
                    user.id.toString().includes(appliedSearchTerm) ||
                    (user.email &&
                        user.email.toLowerCase().includes(appliedSearchTerm.toLowerCase()))
            );
        }
        return currentUsers;
    }, [users, appliedSearchTerm]);

    // Pagination
    const paginatedUsers = useMemo(() => {
        const startIndex = currentPage * usersPerPage;
        return filteredUsers.slice(startIndex, startIndex + usersPerPage);
    }, [filteredUsers, currentPage, usersPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleUsersPerPageChange = (event) => {
        setUsersPerPage(Number(event.target.value));
        setCurrentPage(0);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleMoreOptionsClick = useCallback((event, userId) => {
        setUserMenuAnchorEl(event.currentTarget);
        setSelectedUserId(userId);
    }, []);

    const handleUserMenuClose = useCallback(() => {
        setUserMenuAnchorEl(null);
        setSelectedUserId(null);
    }, []);

    const handleUpdateUserStatus = useCallback(
        (newStatus) => {
            if (selectedUserId !== null) {
                setUsers((prevUsers) =>
                    prevUsers.map((user) =>
                        user.id === selectedUserId ? { ...user, status: newStatus } : user
                    )
                );
                handleUserMenuClose();
            }
        },
        [selectedUserId, handleUserMenuClose]
    );

    const handleSearchInputChange = (event) => {
        setSearchInputValue(event.target.value);
    };

    const handleSearchOnEnter = (event) => {
        if (event.key === "Enter") {
            setAppliedSearchTerm(searchInputValue);
            setCurrentPage(0);
        }
    };

    const handleClearSearch = () => {
        setSearchInputValue("");
        setAppliedSearchTerm("");
        setCurrentPage(0);
    };

    useEffect(() => {
        if (searchInputValue === "" && appliedSearchTerm !== "") {
            setAppliedSearchTerm("");
            setCurrentPage(0);
        }
    }, [searchInputValue, appliedSearchTerm]);

    return (
        <Box className="user-panel-container">
            <LoadingSpinner isLoading={loading} />
            <Typography variant="h5" component="h1" gutterBottom>
                Truck Owner
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="All Truck Owner" {...a11yProps(0)} />
                </Tabs>
            </Box>

            <CustomTabPanel value={tabValue} index={0}>
                <Row className="control-bar mb-4 align-items-center">
                    <Col xs={12} md={7} lg={8} className="search-col">
                        <TextField
                            fullWidth
                            placeholder="Search Owner by name, email & mobile number"
                            variant="outlined"
                            size="small"
                            value={searchInputValue}
                            onChange={handleSearchInputChange}
                            onKeyDown={handleSearchOnEnter}
                            InputProps={{
                                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                endAdornment: searchInputValue ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="clear search"
                                            onClick={handleClearSearch}
                                            edge="end"
                                            size="small"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                sx: { borderRadius: "8px" },
                            }}
                        />
                    </Col>
                </Row>

                {deleteErrorMessage && <Alert severity="error">{deleteErrorMessage}</Alert>}
                {deleteSuccessMessage && <Alert severity="success">{deleteSuccessMessage}</Alert>}
                {error && <Alert severity="error">{error}</Alert>}

                {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                        <Card
                            key={user.id}
                            sx={{ borderRadius: 2, boxShadow: 2, mb: 2, p: 2 }}
                        >
                            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
                                    <Avatar
                                        src={user.avatar}
                                        alt={user.name}
                                        sx={{ width: 50, height: 50 }}
                                    >
                                        {!user.avatar && getInitials(user.name)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold">{user.name}</Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <EmailOutlinedIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <PhoneOutlinedIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">{user.mobile_num}</Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                        <Chip
                                            label={user.status}
                                            size="small"
                                            sx={{
                                                fontWeight: "bold",
                                                backgroundColor: user.status === "Active" ? "#E8F5E9" : "#FFEBEE",
                                                color: user.status === "Active" ? "green" : "red",
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleMoreOptionsClick(event, user.id);
                                            }}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={userMenuAnchorEl}
                                            open={openUserMenu && selectedUserId === user.id}
                                            onClose={handleUserMenuClose}
                                        >
                                            <MenuItem onClick={() => handleUpdateUserStatus("Active")}>
                                                Mark as Active
                                            </MenuItem>
                                            <MenuItem onClick={() => handleUpdateUserStatus("Inactive")}>
                                                Mark as Inactive
                                            </MenuItem>
                                        </Menu>
                                    </Box>

                                    <Box>
                                        <Button
                                            variant="text"
                                            size="small"
                                            endIcon={<ArrowRightAltIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const encryptedId = encryptId(user.id);
                                                navigate(`/app/owner?id=${encodeURIComponent(encryptedId)}`);
                                            }}
                                        >
                                            View Details
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Card>
                    ))
                ) : (
                    <Typography textAlign="center">No truck owners found.</Typography>
                )}

                {!loading && filteredUsers.length > 0 && (
                    <Box className="pagination-controls" display="flex" justifyContent="end" alignItems="center" mt={2}>
                        <div className="items-per-page" style={{ display: "flex", alignItems: "center" }}>
                            <Select value={usersPerPage} onChange={handleUsersPerPageChange} size="small">
                                {[5, 10, 25, 50].map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                            <Typography variant="body2" sx={{ ml: 1 }}>per page</Typography>
                        </div>

                        <Typography variant="body2" sx={{ mx: 2 }}>
                            Page {currentPage + 1} of {totalPages}
                        </Typography>

                        <IconButton
                            onClick={() => handlePageChange(null, Math.max(currentPage - 1, 0))}
                            disabled={currentPage === 0}
                        >
                            <KeyboardArrowLeft />
                        </IconButton>
                        <IconButton
                            onClick={() => handlePageChange(null, Math.min(currentPage + 1, totalPages - 1))}
                            disabled={currentPage >= totalPages - 1}
                        >
                            <KeyboardArrowRight />
                        </IconButton>
                    </Box>
                )}
            </CustomTabPanel>
        </Box>
    );
}

export default TruckOwners;
